import type { ComponentProps, ReactElement, ReactNode } from 'react';
import { faSpinnerThird } from '@awesome.me/kit-4a6bf60416/icons/classic/regular';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { Icon } from '@/components/ui/icon';
import { cn } from '@/lib/utils';

const colorSchemeList = ['primary', 'destructive', 'secondary'] as const;
type Scheme = (typeof colorSchemeList)[number];

const perVariantClasses: Record<'solid' | 'outline' | 'ghost', Record<Scheme, string>> = {
  solid: {
    primary: 'bg-primary text-primary-foreground hover:bg-primary/85',
    destructive:
      'bg-destructive text-destructive-foreground hover:bg-destructive/85 focus-visible:ring-destructive/20',
    secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/75',
  },
  outline: {
    primary: 'border-input hover:bg-primary/5 hover:border-primary/50',
    destructive: 'border-destructive hover:bg-destructive/5',
    secondary: 'border-secondary hover:bg-secondary',
  },
  ghost: {
    primary: 'hover:bg-primary/10',
    destructive: 'hover:bg-destructive/10',
    secondary: 'hover:bg-secondary/80',
  },
};

const groupedCompoundVariants = (['solid', 'outline', 'ghost'] as const).flatMap((variant) =>
  colorSchemeList.map((scheme) => ({
    variant,
    colorScheme: scheme,
    class: perVariantClasses[variant][scheme],
  })),
);

const outlineCompoundVariants = [
  {
    variant: 'solid',
    colorScheme: 'primary',
    buttonStyle: 'outline',
    class: 'bg-transparent text-primary border-2 border-primary hover:bg-primary/10',
  },
  {
    variant: 'solid',
    colorScheme: 'secondary',
    buttonStyle: 'outline',
    class: 'bg-transparent text-secondary border-2 border-secondary hover:bg-secondary/10',
  },
] satisfies Array<{
  variant: 'solid';
  colorScheme: Scheme;
  buttonStyle: 'outline';
  class: string;
}>;

const buttonVariants = cva(
  "focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 aria-invalid:border-destructive inline-flex shrink-0 items-center justify-center gap-2 rounded-md text-sm font-medium whitespace-nowrap transition-all outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 [&_svg]:transition-transform [&_svg]:duration-150 [&_svg]:ease-in-out hover:[&_svg]:scale-110",
  {
    variants: {
      variant: {
        solid: '',
        outline: 'bg-background hover:bg-accent border shadow-xs',
        ghost: 'hover:bg-accent',
        link: 'underline-offset-4 hover:underline',
      },
      size: {
        sm: 'h-8 gap-1.5 rounded-md px-3 has-[>svg]:px-2.5',
        md: 'h-9 px-4 py-2 has-[>svg]:px-3',
        lg: 'h-12 text-md rounded-md px-6 has-[>svg]:px-4',
        icon: 'size-9 transition-colors duration-150 ease-in-out',
        'icon-sm': 'size-8 transition-colors duration-150 ease-in-out',
        'icon-lg': 'size-10 transition-colors duration-150 ease-in-out',
      },
      colorScheme: {
        primary: 'text-primary',
        destructive: 'text-destructive',
        secondary: 'text-secondary-foreground',
      },
      shape: {
        sharp: 'rounded-none',
        smooth: 'rounded-md',
        round: 'rounded-full',
      },
      buttonStyle: {
        solid: '',
        outline: '',
      },
    },
    compoundVariants: [
      // Link variant: no padding by default (like real links, inline with text)
      // Use h-auto to size to content
      // Override has-[>svg] padding from size variants
      // Override with px-1 if minimal spacing is needed for inline text
      // Override with py-* if vertical padding is needed for touch targets
      { variant: 'link', size: 'md', class: 'h-auto px-0 py-0 has-[>svg]:px-0' },
      { variant: 'link', size: 'sm', class: 'h-auto px-0 py-0 has-[>svg]:px-0' },
      { variant: 'link', size: 'lg', class: 'h-auto px-0 py-0 has-[>svg]:px-0' },
      ...groupedCompoundVariants,
      ...outlineCompoundVariants,
    ],
    defaultVariants: {
      variant: 'solid',
      size: 'md',
      colorScheme: 'primary',
    },
  },
);

interface ButtonProps extends ComponentProps<'button'>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
  loadingText?: ReactNode;
  loaderPlacement?: 'start' | 'end';
}

function Button({
  className,
  variant,
  size,
  colorScheme,
  shape,
  buttonStyle,
  asChild = false,
  isLoading = false,
  loadingText,
  type = 'button',
  loaderPlacement = 'end',
  ref,
  ...properties
}: ButtonProps) {
  const Comp = asChild ? Slot : 'button';

  const { children, disabled, ...rest } = properties;
  const mergedDisabled = disabled || isLoading;

  const SpinnerIcon = <Icon icon={faSpinnerThird} spin className="opacity-70" />;

  const content = isLoading && loadingText ? loadingText : children;

  if (asChild) {
    return (
      <Comp
        ref={ref}
        type={type}
        className={cn(
          buttonVariants({ variant, size, className, colorScheme, shape, buttonStyle }),
        )}
        data-slot="button"
        aria-busy={isLoading || undefined}
        disabled={mergedDisabled}
        {...rest}
      >
        {children as ReactElement}
      </Comp>
    );
  }

  return (
    <Comp
      ref={ref}
      type={type}
      className={cn(buttonVariants({ variant, size, className, colorScheme, shape, buttonStyle }))}
      data-slot="button"
      aria-busy={isLoading || undefined}
      disabled={mergedDisabled}
      {...rest}
    >
      {isLoading && loaderPlacement === 'start' ? SpinnerIcon : null}
      {content}
      {isLoading && loaderPlacement === 'end' ? SpinnerIcon : null}
    </Comp>
  );
}

export { Button, buttonVariants };
